import { useEffect, useRef, useState } from 'react';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/mygolflinks/id6787204431';
// The golfer app (marketing links to the App Store, not this URL directly).
export const APP_URL = 'https://app.mygolflinks.com';
// LinksOS, the course owner/operator portal — where "Course sign in" goes.
export const COURSE_APP_URL = 'https://course.mygolflinks.com';
export const SITE_URL = 'https://mygolflinks.com';

// Public form endpoint (site-contact edge function). Demo submissions email
// admin@mygolflinks.com, support submissions email support@mygolflinks.com,
// and every submission also lands in the site_leads table.
export const FORM_ENDPOINT = 'https://xpodoqfsygnygexugzhp.supabase.co/functions/v1/site-contact';

export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const set = (selector, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', value);
    };
    set('meta[name="description"]', description);
    set('meta[property="og:title"]', title);
    set('meta[property="og:description"]', description);
    set('meta[property="og:url"]', SITE_URL + window.location.pathname);
  }, [title, description]);
}

export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* Scroll reveal: 20px rise + fade, staggered via --d, fires once. */
export function Reveal({ as: Tag = 'div', d = 0, className = '', style, children, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (prefersReducedMotion()) {
      el.classList.add('in');
      return undefined;
    }
    const ob = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('in');
            ob.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -36px 0px' }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`rv ${className}`} style={{ ...style, '--d': d }} {...rest}>
      {children}
    </Tag>
  );
}

/* Count-up on viewport entry. Used in a handful of places only. */
export function Count({ value, decimals = 0, className = '' }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(() => (0).toFixed(decimals));
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const finish = () => setDisplay(value.toFixed(decimals));
    if (prefersReducedMotion()) {
      finish();
      return undefined;
    }
    let raf = 0;
    const ob = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          ob.disconnect();
          const t0 = performance.now();
          const dur = 850;
          const tick = (now) => {
            const t = Math.min((now - t0) / dur, 1);
            const eased = 1 - Math.pow(1 - t, 4);
            setDisplay((value * eased).toFixed(decimals));
            if (t < 1) raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
        });
      },
      { threshold: 0.6 }
    );
    ob.observe(el);
    return () => {
      ob.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, decimals]);
  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}

/* Scrollspy for the sticky section subnav. Pass a stable array. */
export function useScrollSpy(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const ob = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-25% 0px -65% 0px' }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) ob.observe(el);
    });
    return () => ob.disconnect();
  }, [ids]);
  return active;
}

export const fmtMoney = (n) => '$' + Math.round(n).toLocaleString('en-US');
